import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import {
  Box,
  CardContent,
  CardHeader,
  ConditionalWrapper,
  DynamicYield,
  Grid,
  InlineNotification,
  Portal,
  Skeleton,
  Stack,
  SwogoContainer,
  Typography,
  useDeviceType,
} from '@packages/shared';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { LazyRender, WishlistButtonWithApi } from '@packages/modules';
import type { TrackingBreadcrumbsFragmentFragmentDoc } from '@packages/gql/generated/shopping/graphql';
import { useConfig } from '@packages/utilities';
import { getProductAndFallbackId } from '../../helpers/getProductAndFallbackId/getProductAndFallbackId';
import { AddToBasketButton } from '../AddToBasketButton';
import { ArticleServices } from '../ArticleServices';
import { BonusPoints } from '../BonusPoints';
import { CollapsibleCard } from '../CollapsibleCard';
import { CompactProduct } from '../CompactProduct';
import { DeliveryInfoCard } from '../DeliveryInfoCard';
import { DownloadsCard } from '../DownloadsCard';
import { EcoLabelList } from '../EcoLabelList';
import { EfficiencyFlagList } from '../EfficiencyFlagList';
import { ImageGallery } from '../ImageGallery';
import { ImageGalleryColorDialog } from '../ImageGallery/ImageGalleryColorDialog/ImageGalleryColorDialog';
import { InstallmentCalculatorCallToAction, InstallmentCard } from '../InstallmentCalculator';
import { LegalHintsCard } from '../LegalHintsCard';
import { LoadbeeCard } from '../Loadbee';
import { OocvTable } from '../OocvTable';
import { Price } from '../Price';
import { ProductHeadline } from '../ProductHeadline';
import { ProductHintsCard } from '../ProductHintsCard';
import { QuantitySelection } from '../QuantitySelection';
import { RatingIconsVerified } from '../RatingIconsVerified';
import { Recommendations } from '../Recommendations';
import { SimilarProducts } from '../SimilarProducts';
import { SlotBox } from '../SlotBox';
import { StickyArea } from '../StickyArea';
import { TaxHint } from '../TaxHint';
import { Tracking } from '../Tracking';
import { VariationSelect } from '../VariationSelect';
import { VoucherPrice } from '../VoucherPrice';

export const productDetailsMessages = defineMessages({
  slotTitle: {
    id: 'detailview.grid.headline.details',
    defaultMessage: 'Produktdetails und Serviceinfos',
  },
  expandCardText: {
    id: 'collapsibleCard.expand.text',
    defaultMessage: 'Mehr Produkteigenschaften anzeigen',
  },
  collapseCardText: {
    id: 'collapsibleCard.collapse.text',
    defaultMessage: 'Weniger Produkteigenschaften anzeigen',
  },
});

const ProductVariationDocument = graphql(/* GraphQL */ `
  query ProductVariation(
    $skipCliplister: Boolean! = false
    $skipAplus: Boolean! = false
    $locale: String!
    $lang: String!
    $productId: String!
    $fallbackId: String!
  ) {
    product(id: $productId, fallbackId: $fallbackId, locale: $locale) {
      ...ProductVariationFragment
    }
  }
`);

const ProductVariationFragment = graphql(/* GraphQL */ `
  fragment ProductVariationFragment on Product {
    akl
    sku
    name
    ...ImageGallery
    ...TrackingFragment
    ...ColorPickerFragment
    bonuspoints {
      ...BonusPointsFragment
    }
    delivery {
      deliverySize
    }
    ...DeliveryFragment
    ...EfficiencyFlagListFragment
    availability {
      state
    }
    price {
      value
      currency
      ...ProductPriceFragment
      ...VoucherPriceFragment
    }
    sizeHint
    tireEfficiency {
      link
      flags {
        level
        type
      }
    }
    powerEfficiencyFlags {
      color
      level
      link
      text
      hasOldLabel
    }
    installment(source: "pdv", paymentpause: false) {
      ...InstallmentCalculatorCallToActionFragment
      numberOfInstallments
    }
    dimensions {
      values {
        displayText
      }
      ...DimensionFragment
    }
    # needed to check if SampleOrder Data is available for the product
    sampleOrderType
    # needed for aPlus
    itemOptionCommKey
    # needed for aPlus
    hasServices
    aplus(language: $lang) @skip(if: $skipAplus) {
      ...ArticleServicesFragment
    }
    ecoLabels {
      iconUrl
      text
      serviceLayerId
      license
    }
    downloads {
      link
      type
    }
    legalHints {
      iconUrl
      text
      serviceLayerId
    }
    oocv {
      ...OocvTableFragment
    }
    productHints {
      iconUrl
      text
      serviceLayerId
    }
    seoRatingIcons: seoRating(locale: $locale) {
      product {
        avgRating
        reviewCount
      }
    }
    sellingPoints
    longDescription
    ...HeadlineFragment
    ...LoadbeeFragment
    installment(source: "pdv", paymentpause: false) {
      numberOfInstallments
    }
    ...AddToBasketFragment
    ...RecommendationsFragment
    ...SimilarRecoFragment
    ...CompactProductFragment
  }
`);

export const ProductVariation = ({
  productData,
  breadcrumbData,
  ratingsSlot,
  seoListSlot,
  searchOrigin,
}: {
  breadcrumbData: FragmentType<typeof TrackingBreadcrumbsFragmentFragmentDoc> | undefined | null;
  productData: FragmentType<typeof ProductVariationFragment>;
  ratingsSlot: ReactNode;
  seoListSlot: ReactNode;
  searchOrigin: string;
}) => {
  const { query, locale: language, defaultLocale: defaultLanguage } = useRouter();
  const { locale, formatMessage } = useIntl();
  const imageGalleryRef = useRef<HTMLDivElement>(null);
  const [selectedVariation, setSelectedVariation] = useState<string | null>(null);
  const { akl } = query;
  const {
    dynamicYieldEnabled,
    installmentCalculator: { InstallmentCalculatorCallToActionLayout },
    product: {
      cliplister: { enabled: cliplisterEnabled },
      aplus: { enabled: aplusEnabled },
      voucherPrice: { enabled: voucherPriceEnabled },
      hasSwogo,
    },
  } = useConfig();
  const { isDesktop, isMobile } = useDeviceType();
  const [{ data }] = useQuery({
    query: ProductVariationDocument,
    pause: !selectedVariation,
    variables: {
      ...getProductAndFallbackId(akl, selectedVariation!),
      locale: locale!,
      lang: language || defaultLanguage!,
      skipCliplister: !cliplisterEnabled,
      skipAplus: !aplusEnabled,
    },
    context: useMemo(() => ({ suspense: false }), []),
  });

  const productVariation = getFragmentData(ProductVariationFragment, data?.product ?? productData);

  // Frontend logic - this should be removed once the gql backend supports query batching. -> https://empiriecom.atlassian.net/browse/INFORM-2588
  const { downloads } = productVariation;
  const downloadsNoProductSheet = downloads.filter(
    (download) => download.type !== 'PRODUCT_DATASHEET',
  );

  return (
    <>
      <Tracking
        productData={productVariation}
        navq={searchOrigin}
        breadCrumbData={breadcrumbData}
      />
      <Grid container item rowSpacing={0.5} columnSpacing={5.3}>
        <Grid item xs={12} md={6} lg={7}>
          <ImageGallery
            productData={productVariation}
            ref={imageGalleryRef}
            colorPicker={
              <ImageGalleryColorDialog
                productData={productVariation}
                onChange={setSelectedVariation}
              />
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={5}>
          <Grid
            container
            spacing={1.5}
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            sx={{
              px: { xs: 1.5, md: 1.5, lg: 0 },
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h1">
                {productVariation.name}
              </Typography>
            </Grid>
            {productVariation.seoRatingIcons && productVariation.seoRatingIcons.product && (
              <Grid item xs={12}>
                <RatingIconsVerified
                  avgRating={productVariation.seoRatingIcons.product.avgRating}
                  reviewCount={productVariation.seoRatingIcons.product.reviewCount}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Price priceData={productVariation.price} />
              <TaxHint deliverySize={productVariation.delivery.deliverySize} />
              {
                /* Geizhals Einsprung -> VoucherPrice */
                voucherPriceEnabled && <VoucherPrice priceData={productVariation.price} />
              }
            </Grid>
            {productVariation.bonuspoints && (
              <Grid item xs={12}>
                <BonusPoints bonusPointsData={productVariation.bonuspoints} />
              </Grid>
            )}
            {productVariation.installment &&
              productVariation.installment.numberOfInstallments > 0 && (
                <Grid item xs={12}>
                  <InstallmentCalculatorCallToAction
                    installmentData={productVariation.installment}
                    basePrice={productVariation.price.value}
                  />
                </Grid>
              )}

            {/* TODO: maybe move the grid inside of EfficiencyFlagList, so we dont have to put tireEfficiency
             * and powerEfficiencyFlags AND EfficiencyFlagListFragment in the fragment
             */}
            {(productVariation.tireEfficiency || productVariation.powerEfficiencyFlags) && (
              <Grid item xs={12}>
                <EfficiencyFlagList efficiencyFlagsData={productVariation} />
              </Grid>
            )}
            {dynamicYieldEnabled && <DynamicYield dataDyId="dv_flag" />}
            {hasSwogo && <SwogoContainer position={0} />}
            {/* TODO: ask how quantitySelection should be handled  */}
            <Grid item xs={12}>
              {/** negative margins are in here, so the dividers in QunatitySelection are displayed correctly */}
              <Box marginLeft={{ xs: -1.5, md: 0 }} marginRight={{ xs: -1.5, lg: 0 }}>
                <QuantitySelection key={productVariation.sku} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              {/* TODO: ask UX how margin bottom or spacings in general should be in variation select (its all over the place in zeplin at the moment) */}
              <Stack spacing={3} mb={2.5}>
                {productVariation.dimensions.map(
                  (dimension, i) =>
                    dimension.values.length > 0 && (
                      <VariationSelect
                        dimensionData={dimension}
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        onChange={setSelectedVariation}
                        selectedVariation={selectedVariation}
                        sampleOrderData={
                          productVariation.sampleOrderType !== null
                            ? {
                                akl: productVariation.akl,
                                sku: productVariation.sku,
                                sampleOrderType: productVariation.sampleOrderType,
                              }
                            : null
                        }
                      />
                    ),
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <DeliveryInfoCard productData={productVariation} />
            </Grid>
            {aplusEnabled && productVariation.aplus && (
              <Grid item xs={12}>
                <ArticleServices
                  aPlusData={productVariation.aplus}
                  currency={productVariation.price.currency}
                />
              </Grid>
            )}
            {productVariation.sizeHint && (
              <Grid item xs={12}>
                <InlineNotification severity="info">{productVariation.sizeHint}</InlineNotification>
              </Grid>
            )}
            {isDesktop && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                <WishlistButtonWithApi
                  sku={productVariation.sku}
                  productName={productVariation.name}
                  variant="contained"
                  sx={{ flexShrink: 0 }}
                  availability={productVariation.availability.state}
                />
                <AddToBasketButton productData={productVariation} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <ConditionalWrapper
          condition={isMobile}
          wrapper={(ch) => (
            <LazyRender
              key={`product-details-${productVariation.sku}`}
              fallback={<Skeleton height={550} />}
            >
              {ch}
            </LazyRender>
          )}
        >
          <Box>
            {hasSwogo && <SwogoContainer position={1} />}
            <Typography variant="h2" sx={{ m: 2 }} component="header">
              {formatMessage(productDetailsMessages.slotTitle)}
            </Typography>
            <SlotBox>
              <Grid container columnSpacing={5.75} rowSpacing={1.5}>
                <Grid item xs={12} lg={7}>
                  <Stack spacing={1.5}>
                    <CollapsibleCard
                      cardActionText={{
                        collapseText: formatMessage(productDetailsMessages.collapseCardText),
                        expandText: formatMessage(productDetailsMessages.expandCardText),
                      }}
                      collapseChildren={
                        productVariation.oocv &&
                        productVariation.oocv.map((oocvTable, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Stack sx={{ p: 2 }} spacing={1.5} key={idx}>
                            <OocvTable oocvTableData={oocvTable} />
                          </Stack>
                        ))
                      }
                      collapsedSize="6rem"
                    >
                      <CardHeader
                        sx={{ py: 1, px: 1.5, borderBottom: 1, borderColor: 'grey.main' }}
                        title={<ProductHeadline productData={productVariation} />}
                      />
                      <CardContent sx={{ pb: 0 }}>
                        <Stack gap={1}>
                          <Box component="ul" sx={{ m: 0, pl: 2 }} aria-labelledby="selling-points">
                            {productVariation.sellingPoints.map((sellingPoint, idx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <Box component="li" sx={{ typography: 'body2' }} key={idx}>
                                {sellingPoint}
                              </Box>
                            ))}
                          </Box>
                          {productVariation.longDescription && (
                            <Typography
                              variant="body2" /* eslint-disable-next-line react/no-danger */
                              dangerouslySetInnerHTML={{ __html: productVariation.longDescription }}
                            />
                          )}
                        </Stack>
                      </CardContent>
                    </CollapsibleCard>
                    <LoadbeeCard productData={productVariation} />
                  </Stack>
                </Grid>
                <Grid item xs={12} lg={5}>
                  <Stack spacing={1.5}>
                    {productVariation.ecoLabels.length > 0 && (
                      <EcoLabelList ecoLabels={productVariation.ecoLabels} />
                    )}
                    {productVariation.productHints.length > 0 && (
                      <ProductHintsCard productHintsData={productVariation.productHints} />
                    )}
                    {downloadsNoProductSheet.length > 0 && (
                      <DownloadsCard downloadsData={downloadsNoProductSheet} />
                    )}
                    {productVariation.legalHints.length > 0 && (
                      <LegalHintsCard legalHintsData={productVariation.legalHints} />
                    )}
                    {productVariation.installment &&
                      productVariation.installment.numberOfInstallments > 0 &&
                      InstallmentCalculatorCallToActionLayout.layout === 'Layout3' &&
                      InstallmentCalculatorCallToActionLayout.openCalculatorIn.type === 'Card' && (
                        <InstallmentCard
                          basePrice={productVariation.price.value}
                          cardID={
                            InstallmentCalculatorCallToActionLayout.openCalculatorIn.calculatorID
                          }
                        />
                      )}
                    {dynamicYieldEnabled && (
                      <>
                        <DynamicYield dataDyId="dv_badge" />
                        <DynamicYield dataDyId="dv-horizontal-banner" />
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </SlotBox>
          </Box>
        </ConditionalWrapper>
      </Grid>
      <Grid item xs={12}>
        <Recommendations recosData={productVariation} />
      </Grid>
      {ratingsSlot}
      <Grid item xs={12}>
        <SimilarProducts recoData={productVariation} />
      </Grid>
      {seoListSlot}
      <Portal>
        <StickyArea attachedNodeRef={imageGalleryRef}>
          <CompactProduct compactProductData={productVariation} withProductName={isDesktop} />
          <Stack direction="row" gap={1} alignItems="center" sx={{ bgcolor: 'common.white' }}>
            <WishlistButtonWithApi
              variant="contained"
              sku={productVariation.sku}
              sx={{ flexShrink: 0 }}
              productName={productVariation.name}
              availability={productVariation.availability.state}
            />
            <AddToBasketButton productData={productVariation} />
          </Stack>
        </StickyArea>
      </Portal>
    </>
  );
};
